import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  LinearProgress,
  Stack,
} from '@mui/material';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';


const StatCard: React.FC<{ title: string; value: number | string }> = ({ title, value }) => (
  <Card>
    <CardContent>
      <Typography variant="overline" color="textSecondary">{title}</Typography>
      <Typography variant="h4">{value}</Typography>
    </CardContent>
  </Card>
);

const AdminDashboard: React.FC = () => {
  const { keycloak, login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<any>(null);
  const [insights, setInsights] = useState<any>(null);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [dedupeResult, setDedupeResult] = useState<any>(null);
  const [dedupeRunning, setDedupeRunning] = useState<boolean>(false);
  // Monetization metrics (lightweight, from Redis counters)
  const [adsMetrics, setAdsMetrics] = useState<any | null>(null);
  // AI Summaries queue status
  const [summaryQueue, setSummaryQueue] = useState<any | null>(null);
  const [refreshMissingRunning, setRefreshMissingRunning] = useState<boolean>(false);
  const [refreshMissingResult, setRefreshMissingResult] = useState<any | null>(null);

  const PUBLIC_MODE = (process.env.REACT_APP_PUBLIC_MODE || 'true') === 'true';

  // Long-running admin tasks statuses
  const [memberTask, setMemberTask] = useState<any>(null);
  const [billsTask, setBillsTask] = useState<any>(null);
  const [billSummariesTask, setBillSummariesTask] = useState<any>(null);

  const isAdministrator = useMemo(() => {
    const ADMIN_NAME = (process.env.REACT_APP_ADMIN_GROUP_NAME || 'administrator');
    const tokenParsed: any = keycloak?.tokenParsed || {};
    const groups: string[] = tokenParsed?.groups || tokenParsed?.realm_access?.groups || [] as any;
    // Policy: Only group membership confers admin rights
    const hasGroup = Array.isArray(groups) && groups.some(g => String(g).replace(/^\//, '') === ADMIN_NAME);
    return hasGroup;
  }, [keycloak]);

  useEffect(() => {
    if (!isAdministrator) {
      // In public mode or when not authenticated, don't redirect; just show notice
      setLoading(false);
      return;
    }

    // Ensure API requests include token
    try {
      if (keycloak?.token) {
        apiService.setToken(keycloak.token);
      }
    } catch {}

    (async () => {
      try {
        const [metricsResp, syncResp, insightsResp, adsResp, sumQ] = await Promise.all([
          apiService.getAdminMetrics(),
          apiService.getAdminSyncStatus(),
          apiService.getAdminEngagementInsights(7).catch(() => ({ data: null } as any)),
          apiService.getAdminAdsenseMetrics().catch(() => ({ data: null } as any)),
          apiService.getSummaryQueueStatus().catch(() => ({ data: null } as any)),
        ]);
        setMetrics(metricsResp.data);
        setSyncStatus(syncResp.data);
        setInsights(insightsResp?.data || null);
        setAdsMetrics(adsResp?.data || null);
        setSummaryQueue(sumQ?.data || null);
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to load admin data');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdministrator, keycloak]);

  // Poll background task statuses every 2s
  useEffect(() => {
    if (!isAdministrator) return;

    let interval: any;
    const poll = async () => {
      try {
        if (keycloak?.token) apiService.setToken(keycloak.token);
        const [mStat, bStat, bsStat, sumQ] = await Promise.all([
          apiService.getRefreshMemberSummariesStatus().catch(() => ({ data: null } as any)),
          apiService.getSyncBillsStatus().catch(() => ({ data: null } as any)),
          apiService.getRefreshBillSummariesStatus().catch(() => ({ data: null } as any)),
          apiService.getSummaryQueueStatus().catch(() => ({ data: null } as any)),
        ]);
        const nextMember = mStat?.data || null;
        const nextBills = bStat?.data || null;
        const nextBillSummaries = bsStat?.data || null;

        setMemberTask((prev: any) => {
          if (!nextMember && prev?.running) return prev; // keep running state to prevent flicker
          return nextMember;
        });
        setBillsTask((prev: any) => {
          if (!nextBills && prev?.running) return prev;
          return nextBills;
        });
        setBillSummariesTask((prev: any) => {
          if (!nextBillSummaries && prev?.running) return prev;
          return nextBillSummaries;
        });
        setSummaryQueue(sumQ?.data || null);
      } catch (e) {
        // ignore
      }
    };
    poll();
    interval = setInterval(poll, 2000);
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAdministrator, keycloak]);

  if (!isAdministrator) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Administrator Dashboard
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Typography variant="body1" gutterBottom>
            You must be an administrator to view this page.
          </Typography>
          {PUBLIC_MODE ? (
            <Typography variant="body2" color="textSecondary">
              Public mode is enabled, so authentication is disabled. To access admin tools, set REACT_APP_PUBLIC_MODE=false and configure Keycloak.
            </Typography>
          ) : (
            <Button variant="contained" onClick={login}>Login</Button>
          )}
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Loading admin metrics..." />;
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Administrator Dashboard</Typography>
        <Paper sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  const totals = metrics?.totals || {};
  const top = metrics?.top_discussed_bills || [];

  const handleStartMemberSummaries = async () => {
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      // Optimistically mark as running to avoid initial flicker while backend enqueues
      setMemberTask((prev: any) => ({ ...(prev || {}), running: true, percent: (prev && typeof prev.percent === 'number') ? prev.percent : 0, message: prev?.message || 'Queued…' }));
      await apiService.triggerRefreshMemberSummaries();
      // status will be picked up by poller
    } catch (e) {
      // ignore; error will show in subsequent polls if any
    }
  };

  const handleStartBillsSync = async () => {
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      // Optimistically mark as running to avoid initial flicker
      setBillsTask((prev: any) => ({ ...(prev || {}), running: true, percent: (prev && typeof prev.percent === 'number') ? prev.percent : 0, message: prev?.message || 'Queued…' }));
      await apiService.triggerSyncBills();
    } catch (e) {
      // ignore
    }
  };

  const handleStartBillSummariesRefresh = async () => {
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      // Optimistically mark as running to avoid initial flicker
      setBillSummariesTask((prev: any) => ({ ...(prev || {}), running: true, percent: (prev && typeof prev.percent === 'number') ? prev.percent : 0, message: prev?.message || 'Queued…' }));
      await apiService.triggerRefreshBillSummaries();
    } catch (e) {
      // ignore
    }
  };

  const handleDedupe = async () => {
    const ok = window.confirm('This will scan and remove unwanted duplicates from bills, members, amendments, and actions. Continue?');
    if (!ok) return;
    setDedupeRunning(true);
    setDedupeResult(null);
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      await apiService.postAdminDedupe();
      // Poll status until finished
      const poll = async () => {
        try {
          if (keycloak?.token) apiService.setToken(keycloak.token);
          const st = await apiService.getAdminDedupeStatus();
          const status = st.data || {};
          if (!status?.running && status?.finished_at) {
            const res = status?.result || {};
            setDedupeResult(res?.report || res);
            setDedupeRunning(false);
            // Refresh metrics after cleanup
            try {
              const metricsResp = await apiService.getAdminMetrics();
              setMetrics(metricsResp.data);
            } catch {}
            return true;
          }
        } catch (e) {
          // ignore transient errors
        }
        return false;
      };
      // immediate check then interval
      const doneNow = await poll();
      if (doneNow) return;
      const interval = setInterval(async () => {
        const done = await poll();
        if (done) clearInterval(interval);
      }, 2000);
    } catch (e: any) {
      setDedupeResult({ error: e?.response?.data?.detail || 'Failed to dedupe' });
      setDedupeRunning(false);
    }
  };

  const handleRefreshMissingProfiles = async () => {
    const ok = window.confirm('This will enqueue refresh jobs for members missing a name or photo. Continue?');
    if (!ok) return;
    setRefreshMissingRunning(true);
    setRefreshMissingResult(null);
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      const resp = await apiService.refreshMissingMemberProfiles();
      setRefreshMissingResult(resp?.data || resp);
    } catch (e: any) {
      setRefreshMissingResult({ error: e?.response?.data?.detail || 'Failed to enqueue refresh' });
    } finally {
      setRefreshMissingRunning(false);
    }
  };

  return (
    <Box>
      {/* Engagement Insights */}
      {insights && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Engagement Insights (Last {insights?.window?.days ?? 7} days)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}><StatCard title="Follows" value={insights.window_totals?.follows ?? 0} /></Grid>
                <Grid item xs={6} md={3}><StatCard title="Bookmarks" value={insights.window_totals?.bookmarks ?? 0} /></Grid>
                <Grid item xs={6} md={3}><StatCard title="Comments" value={insights.window_totals?.comments ?? 0} /></Grid>
                <Grid item xs={6} md={3}><StatCard title="Reactions" value={insights.window_totals?.reactions ?? 0} /></Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary">Active Users</Typography>
                  <Typography variant="h4">{insights.window_totals?.active_users ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>Top Followed Bills</Typography>
                  {(insights.top_followed_bills || []).slice(0,5).map((b: any, idx: number) => (
                    <Typography key={idx} variant="body2" sx={{ display: 'block' }}>
                      {b.id}: {b.follower_count}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>Top Commented Bills</Typography>
                  {(insights.top_commented_bills || []).slice(0,5).map((b: any, idx: number) => (
                    <Typography key={idx} variant="body2" sx={{ display: 'block' }}>
                      {b.id}: {b.comment_count}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>Top Reacted Bills</Typography>
                  {(insights.top_reacted_bills || []).slice(0,5).map((b: any, idx: number) => (
                    <Typography key={idx} variant="body2" sx={{ display: 'block' }}>
                      {b.id}: {b.reaction_count}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}
      <Typography variant="h4" component="h1" gutterBottom>
        Administrator Dashboard
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/admin/system')}>System Status</Button>
        <Button variant="outlined" onClick={() => navigate('/admin/sync-inspector')}>Sync Inspector</Button>
        <Button variant="outlined" onClick={() => navigate('/admin/social-handles')}>Member Social Handles</Button>
        <Button variant="outlined" onClick={() => navigate('/admin/ollama')}>Ollama Endpoints</Button>
        <Button variant="outlined" onClick={() => navigate('/admin/adsense')}>AdSense Settings</Button>
        <Button variant="outlined" onClick={() => navigate('/admin/celery')}>Celery Queues</Button>
        <Button variant="outlined" onClick={() => navigate('/admin/support')}>Support Messages</Button>
        <Button variant="contained" onClick={() => navigate('/admin/civic-response')}>Civic Response Studio (Admin)</Button>
      </Stack>
      {
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            {(() => {
              const queued = typeof summaryQueue?.queued === 'number' ? summaryQueue.queued : 0;
              const engineConfigured = !!summaryQueue?.engine_configured && (summaryQueue?.endpoints_enabled ?? 0) > 0;
              const chipColor: any = queued > 0 ? (engineConfigured ? 'default' : 'warning') : 'success';
              return (
                <Chip
                  label={`Summaries: ${queued} queued`}
                  color={chipColor}
                />
              );
            })()}
            <Chip label={`AI Endpoints enabled: ${(summaryQueue?.endpoints_enabled ?? 0)}/${(summaryQueue?.endpoints_total ?? 0)}`} variant="outlined" />
            <Typography variant="body2" color="textSecondary">
              {summaryQueue?.message || 'Queued summary jobs are processed by the worker in background.'}
            </Typography>
          </Stack>
        </Paper>
      }
      {adsMetrics && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Monetization</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Chip label={`AdSense: ${adsMetrics?.config?.enabled ? 'Enabled' : 'Disabled'}`} color={adsMetrics?.config?.enabled ? 'success' : 'default'} />
            {adsMetrics?.config?.publisher_id && (
              <Chip label={`Publisher: ${String(adsMetrics.config.publisher_id).slice(0, 12)}…`} variant="outlined" />
            )}
            <Chip label={`Impressions (24h): ${adsMetrics?.totals?.impressions ?? 0}`} />
            <Chip label={`Clicks (24h): ${adsMetrics?.totals?.clicks ?? 0}`} />
            <Button variant="outlined" onClick={() => navigate('/admin/adsense')}>Configure AdSense</Button>
          </Stack>
        </Paper>
      )}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}><StatCard title="Bills in Database" value={totals.bills ?? 0} /></Grid>
        <Grid item xs={12} md={3}><StatCard title="Members in Database" value={totals.members ?? 0} /></Grid>
        <Grid item xs={12} md={3}><StatCard title="Chat Sessions" value={totals.chat_sessions ?? 0} /></Grid>
        <Grid item xs={12} md={3}><StatCard title="Chat Messages" value={totals.chat_messages ?? 0} /></Grid>
        <Grid item xs={12} md={3}><StatCard title="Summaries Due" value={totals.summaries_due ?? 0} /></Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Admin Actions</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Refresh All Member AI Summaries</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Button variant="contained" onClick={handleStartMemberSummaries} disabled={!!memberTask?.running}>Start</Button>
                {memberTask?.running && <Chip label="Running" color="warning" size="small" />}
                {memberTask?.finished_at && !memberTask?.running && <Chip label="Finished" color="success" size="small" />}
                {memberTask?.error && <Chip label="Error" color="error" size="small" />}
              </Stack>
              {(memberTask && (memberTask.running || memberTask.finished_at || memberTask.error || typeof memberTask.percent === 'number')) ? (
                <>
                  <Box sx={{ mb: 1 }}>
                    {memberTask?.percent !== null && memberTask?.percent !== undefined ? (
                      <LinearProgress variant="determinate" value={memberTask.percent} />
                    ) : (
                      <LinearProgress />
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {`Completed: ${memberTask?.completed ?? 0} / ${memberTask?.total ?? 0}`}
                    {memberTask?.failed ? `, Failed: ${memberTask.failed}` : ''}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="textSecondary">Idle</Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Sync Bills (daily refresh)</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Button variant="contained" onClick={handleStartBillsSync} disabled={!!billsTask?.running}>Start</Button>
                {billsTask?.running && <Chip label="Running" color="warning" size="small" />}
                {billsTask?.finished_at && !billsTask?.running && <Chip label="Finished" color="success" size="small" />}
                {billsTask?.error && <Chip label="Error" color="error" size="small" />}
              </Stack>
              {(billsTask && (billsTask.running || billsTask.finished_at || billsTask.error || typeof billsTask.percent === 'number')) ? (
                <>
                  <Box sx={{ mb: 1 }}>
                    {billsTask?.percent !== null && billsTask?.percent !== undefined ? (
                      <LinearProgress variant="determinate" value={billsTask.percent} />
                    ) : (
                      <LinearProgress />
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {`Completed: ${billsTask?.completed ?? 0} / ${billsTask?.total ?? 0}`}
                    {billsTask?.failed ? `, Failed: ${billsTask.failed}` : ''}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="textSecondary">Idle</Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Refresh All Bill AI Summaries</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Button variant="contained" onClick={handleStartBillSummariesRefresh} disabled={!!billSummariesTask?.running}>Start</Button>
                {billSummariesTask?.running && <Chip label="Running" color="warning" size="small" />}
                {billSummariesTask?.finished_at && !billSummariesTask?.running && <Chip label="Finished" color="success" size="small" />}
                {billSummariesTask?.error && <Chip label="Error" color="error" size="small" />}
              </Stack>
              {(billSummariesTask && (billSummariesTask.running || billSummariesTask.finished_at || billSummariesTask.error || typeof billSummariesTask.percent === 'number')) ? (
                <>
                  <Box sx={{ mb: 1 }}>
                    {billSummariesTask?.percent !== null && billSummariesTask?.percent !== undefined ? (
                      <LinearProgress variant="determinate" value={billSummariesTask.percent} />
                    ) : (
                      <LinearProgress />
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {billSummariesTask?.message || ''}
                    {typeof billSummariesTask?.completed === 'number' && typeof billSummariesTask?.total === 'number'
                      ? ` — Completed: ${billSummariesTask.completed} / ${billSummariesTask.total}`
                      : ''}
                    {typeof billSummariesTask?.failed === 'number' && billSummariesTask.failed > 0 ? `, Failed: ${billSummariesTask.failed}` : ''}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="textSecondary">Idle</Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Remove Unwanted Duplicates</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Button variant="contained" color="secondary" onClick={handleDedupe} disabled={dedupeRunning}>
                  {dedupeRunning ? 'Running...' : 'Remove Duplicates'}
                </Button>
                {dedupeRunning && <Chip label="Working" color="warning" size="small" />}
              </Stack>
              {dedupeResult && (
                <Box>
                  {dedupeResult.error ? (
                    <Typography color="error" variant="body2">{dedupeResult.error}</Typography>
                  ) : (
                    <>
                      <Typography variant="body2" gutterBottom>Cleanup summary:</Typography>
                      <Grid container spacing={1}>
                        {Object.entries(dedupeResult).map(([k, v]: any) => (
                          <Grid item key={k}>
                            <Chip label={`${k}: ${typeof v === 'object' ? (v.duplicates_removed ?? v.status ?? '') : String(v)}`} size="small" />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Maintenance shortcuts */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Maintenance</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <Button variant="contained" color="secondary" disabled={refreshMissingRunning} onClick={handleRefreshMissingProfiles}>
            {refreshMissingRunning ? 'Enqueuing…' : 'Refresh members missing name/photo'}
          </Button>
          {refreshMissingResult && (
            <Typography variant="body2" color={refreshMissingResult.error ? 'error' : 'textSecondary'}>
              {refreshMissingResult.error ? (
                <>Error: {String(refreshMissingResult.error)}</>
              ) : (
                <>Matched {refreshMissingResult.matched || 0}, enqueued {refreshMissingResult.enqueued || 0}{(refreshMissingResult.skipped_dedup || 0) > 0 ? `, skipped (dedupe) ${refreshMissingResult.skipped_dedup}` : ''}.</>
              )}
            </Typography>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Sync Status</Typography>
        {!syncStatus ? (
          <Typography variant="body2" color="textSecondary">No sync data available.</Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Bills</Typography>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                <Chip label={`New (24h): ${syncStatus?.bills?.new_synced_last_24h ?? 0}`} color="primary" size="small" />
                <Chip label={`Updated (24h): ${syncStatus?.bills?.updated_last_24h ?? 0}`} color="success" size="small" />
                <Chip label={`Last Create: ${syncStatus?.bills?.last_created_at ? new Date(syncStatus.bills.last_created_at).toLocaleString() : 'n/a'}`} size="small" variant="outlined" />
                <Chip label={`Last Update: ${syncStatus?.bills?.last_updated_at ? new Date(syncStatus.bills.last_updated_at).toLocaleString() : 'n/a'}`} size="small" variant="outlined" />
                <Chip label={`Total: ${syncStatus?.bills?.total ?? 0}`} size="small" />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Members</Typography>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                <Chip label={`Updated (24h): ${syncStatus?.members?.updated_last_24h ?? 0}`} color="success" size="small" />
                <Chip label={`Last Update: ${syncStatus?.members?.last_updated_at ? new Date(syncStatus.members.last_updated_at).toLocaleString() : 'n/a'}`} size="small" variant="outlined" />
                <Chip label={`Total: ${syncStatus?.members?.total ?? 0}`} size="small" />
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Upcoming AI Summary Recipients</Typography>
        {Array.isArray(metrics?.summary_schedule_preview) && metrics.summary_schedule_preview.length > 0 ? (
          <Grid container spacing={1}>
            {metrics.summary_schedule_preview.map((m: any) => (
              <Grid item xs={12} md={6} lg={4} key={m.bioguide_id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                      onClick={() => navigate(`/members/${m.bioguide_id}`)}
                    >
                      {m.name || m.bioguide_id}
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      <Chip label={`${m.party || 'N/A'}-${m.state || ''}`} size="small" />
                      <Chip label={m.status || 'due'} color="warning" size="small" />
                      <Chip label={`Last: ${m.last_generated_at ? new Date(m.last_generated_at).toLocaleDateString() : 'never'}`} size="small" variant="outlined" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="textSecondary">No members are currently due for summary refresh.</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Recently Updated AI Summaries</Typography>
        {Array.isArray(metrics?.recent_member_summaries) && metrics.recent_member_summaries.length > 0 ? (
          <Grid container spacing={1}>
            {metrics.recent_member_summaries.map((m: any) => (
              <Grid item xs={12} md={6} lg={4} key={`${m.bioguide_id}-${m.generated_at}`}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                      onClick={() => navigate(`/members/${m.bioguide_id}`)}
                    >
                      {m.name || m.bioguide_id}
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      <Chip label={`${m.party || 'N/A'}-${m.state || ''}`} size="small" />
                      <Chip label={`Updated: ${m.generated_at ? new Date(m.generated_at).toLocaleString() : 'unknown'}`} size="small" variant="outlined" />
                      {m?.stats?.cross_aisle_grade !== undefined && (
                        <Chip label={`Cross-aisle: ${m.stats.cross_aisle_grade}/100`} size="small" color="success" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="textSecondary">No AI summaries have been generated yet.</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Top Discussed Bills</Typography>
        {top.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No bill discussions recorded yet.</Typography>
        ) : (
          <Grid container spacing={2}>
            {top.map((b: any, idx: number) => (
              <Grid item xs={12} md={6} lg={4} key={idx}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {(b.bill_type || '').toUpperCase()} {b.bill_number} (Congress {b.congress})
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip label={`Count: ${b.count}`} size="small" />
                      <Chip label={`ID: ${b.bill_id}`} size="small" variant="outlined" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
